import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui';
import { useSession } from '@/lib/auth';
import { useWaitlist } from '@/hooks/useWaitlist';

type WaitlistCtaProps = {
  eventId: string;
  localityId: string;
  localityName: string;
};

/** Spinner premium inline (sin dependencias): aro cyan que gira. */
function Spinner({ label }: { label: string }): JSX.Element {
  return (
    <span
      role="status"
      aria-label={label}
      className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-cyan/30 border-t-cyan"
    />
  );
}

/**
 * WaitlistCta — la cara del fan de la lista de espera (SDD-029), acento premium
 * azul/cian para diferenciarla de la acción de compra.
 *
 * Se muestra SÓLO cuando la localidad está agotada. Estados:
 *   - sin sesión → invita a entrar (la fila necesita identidad); al tocar,
 *     redirige a login conservando el retorno a este evento;
 *   - anotado → «Eres el #X en la fila» + botón secundario para salir;
 *   - no anotado → botón «Unirme a la lista de espera».
 *
 * Toda acción muestra su spinner y cualquier error se rinde como alerta; nunca
 * falla en silencio.
 */
export function WaitlistCta({ eventId, localityId, localityName }: WaitlistCtaProps): JSX.Element {
  const { t } = useTranslation('events');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: sessionLoading } = useSession();
  const isLoggedIn = !!user;

  const {
    enrolled,
    position,
    isLoading,
    error,
    join,
    leave,
    isMutating,
    actionError,
  } = useWaitlist({ eventId, localityId, enabled: isLoggedIn });

  // Sin sesión: la fila necesita identidad. Mandamos a login con retorno acá.
  if (!sessionLoading && !isLoggedIn) {
    const from = encodeURIComponent(`${location.pathname}${location.search}`);
    return (
      <div className="flex flex-col items-stretch gap-1.5 md:items-end">
        <WaitlistJoinButton
          label={t('waitlist.join')}
          onClick={() => navigate(`/login?from=${from}`)}
        />
        <p className="text-xs text-fc-muted md:text-right">{t('waitlist.loginHint')}</p>
      </div>
    );
  }

  // Cargando la sesión o la primera lectura de la fila.
  if (sessionLoading || isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-fc-muted">
        <Spinner label={t('waitlist.loading')} />
        <span>{t('waitlist.loading')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-stretch gap-1.5 md:items-end">
      {enrolled ? (
        <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center">
          <span
            className="inline-flex items-center gap-2 rounded-card border border-cyan/40 bg-cyan/10 px-3 py-2 text-sm font-semibold text-teal"
            aria-live="polite"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-cyan" aria-hidden="true" />
            {position !== null
              ? t('waitlist.position', { position })
              : t('waitlist.enrolled')}
          </span>
          <Button
            variant="secondary"
            size="md"
            disabled={isMutating}
            onClick={() => {
              void leave();
            }}
          >
            {isMutating ? <Spinner label={t('waitlist.leaving')} /> : null}
            {t('waitlist.leave')}
          </Button>
        </div>
      ) : (
        <WaitlistJoinButton
          label={t('waitlist.join')}
          busy={isMutating}
          busyLabel={t('waitlist.joining')}
          onClick={() => {
            void join();
          }}
        />
      )}

      {/* Alertas — la fila no puede fallar en silencio. */}
      {actionError && (
        <p role="alert" className="text-xs font-medium text-danger md:text-right">
          {t('waitlist.actionError')}
        </p>
      )}
      {error && !actionError && (
        <p role="alert" className="text-xs font-medium text-danger md:text-right">
          {t('waitlist.statusError')}
        </p>
      )}
      {!enrolled && !actionError && (
        <p className="text-xs text-fc-muted md:text-right">
          {t('waitlist.hint', { name: localityName })}
        </p>
      )}
    </div>
  );
}

/** Botón de acción con acento premium cyan→teal y estado ocupado. */
function WaitlistJoinButton({
  label,
  busy = false,
  busyLabel,
  onClick,
}: {
  label: string;
  busy?: boolean;
  busyLabel?: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <Button
      variant="primary"
      size="md"
      disabled={busy}
      onClick={onClick}
      className="bg-gradient-to-r from-cyan to-teal text-white shadow-sm hover:from-teal hover:to-cyan focus-visible:outline-cyan"
    >
      {busy ? <Spinner label={busyLabel ?? label} /> : null}
      {busy ? (busyLabel ?? label) : label}
    </Button>
  );
}
